/**
 * Filters that exist to bound an otherwise unbounded read, and so must survive a dialog session
 * whatever the user does.
 *
 * Two mechanisms grew separately for this: AuditTrailTable took a `lockedFilters` array of keys and
 * the reporting pages set `isPersistent` on the filter object. They are collapsed here because the
 * dialog now returns the whole filter map: anything it is not told to protect is dropped on Apply,
 * so "protected" has to be one thing the dialog is given rather than two the callers each express
 * differently.
 */

/**
 * Keys the dialog must not let the user remove or clear.
 *
 * Reads both the explicit list a caller passes and the `isPersistent` flag on the filters
 * themselves, so existing callers keep working without being rewritten in the same change.
 *
 * `isPersistent` is read off the filter, which is where the reporting pages set it. It used to be
 * read off the filter's statements, where nothing ever wrote it, so the guard silently never fired
 * and the persistent date-range chip was removable.
 *
 * @param {Record<string, { isPersistent?: boolean }>} filters
 * @param {string[]} [lockedFilterKeys] Keys the caller protects regardless of the filter's own flag
 * @returns {Set<string>}
 */
export function getMandatoryFilterKeys(filters = {}, lockedFilterKeys = []) {
  const persistent = Object.entries(filters ?? {})
    .filter(([, filter]) => filter?.isPersistent === true)
    .map(([filterKey]) => filterKey);

  return new Set([...(lockedFilterKeys ?? []), ...persistent]);
}

/**
 * Whether a single filter may be removed or cleared.
 *
 * @param {string} filterKey
 * @param {Set<string>} mandatoryKeys
 * @returns {boolean}
 */
export function isFilterRemovable(filterKey, mandatoryKeys) {
  return !mandatoryKeys.has(filterKey);
}

/**
 * Apply a Clear to a filter map, keeping the protected entries and anything out of scope.
 *
 * Scope is what the user can see: inside a group, a Clear covers that group's criteria and nothing
 * else, since emptying the groups beside it would be clearing filters not reachable from there.
 *
 * Returns a new object; the dialog holds a draft and must not mutate what the caller passed in,
 * since Cancel has to leave the caller's filters exactly as they were.
 *
 * @param {Record<string, unknown>} filters
 * @param {Set<string>} mandatoryKeys
 * @param {string[]|null} [scopeKeys] Criteria the clear covers. Every filter when null or omitted.
 * @returns {Record<string, unknown>}
 */
export function clearFilters(filters, mandatoryKeys, scopeKeys = null) {
  const inScope = scopeKeys === null ? null : new Set(scopeKeys);

  return Object.fromEntries(
    Object.entries(filters ?? {}).filter(([filterKey]) => mandatoryKeys.has(filterKey)
      || (inScope !== null && !inScope.has(filterKey))),
  );
}

/**
 * Restore `isPersistent` on filters loaded from local storage or saved views.
 *
 * Older saved filters may not have this flag, leaving their chips removable.
 * Only the flag is copied, so user-modified ranges stay unchanged.
 *
 * @param {Record<string, object>} filters Filters as restored
 * @param {Record<string, { isPersistent?: boolean }>} defaultFilters
 * @returns {Record<string, object>}
 */
export function withPersistentFlags(filters, defaultFilters = {}) {
  return Object.fromEntries(
    Object.entries(filters ?? {}).map(([filterKey, filter]) => [
      filterKey,
      defaultFilters?.[filterKey]?.isPersistent === true ? { ...filter, isPersistent: true } : filter,
    ]),
  );
}
